import { useTranslation } from 'react-i18next'
import { useState, useMemo } from 'react'
import { CustomerPipelineFilters, QualifiedProspectsFilters } from '../../types'
import Overview from './Overview'
import SalesActivity from './SalesActivity'
import PipelineAnalysis from './PipelineAnalysis'
import { LayoutDashboard, Activity, TrendingUp } from 'lucide-react'

interface QualifiedProspectsTabProps {
    filters: CustomerPipelineFilters
}

type SubTab = 'overview' | 'sales-activity' | 'pipeline'

const QualifiedProspectsTab: React.FC<QualifiedProspectsTabProps> = ({
    filters,
}) => {
    const { t } = useTranslation()
    const [activeSubTab, setActiveSubTab] = useState<SubTab>('overview')

    // Convert CustomerPipelineFilters to QualifiedProspectsFilters
    const qualifiedFilters: QualifiedProspectsFilters = useMemo(
        () => ({
            from_date: filters.gte,
            to_date: filters.lte,
            inactive_weeks: 4,
            limit: 30,
            granularity: 'week',
        }),
        [filters.gte, filters.lte]
    )

    const subTabs = [
        {
            value: 'overview' as SubTab,
            label: t('qualified_prospects.overview'),
            icon: LayoutDashboard,
            color: 'blue',
        },
        {
            value: 'sales-activity' as SubTab,
            label: t('qualified_prospects.sales_activity'),
            icon: Activity,
            color: 'purple',
        },
        {
            value: 'pipeline' as SubTab,
            label: t('qualified_prospects.pipeline_analysis'),
            icon: TrendingUp,
            color: 'green',
        },
    ]

    const renderSubTabContent = () => {
        switch (activeSubTab) {
            case 'overview':
                return <Overview filters={qualifiedFilters} />
            case 'sales-activity':
                return <SalesActivity filters={qualifiedFilters} />
            case 'pipeline':
                return <PipelineAnalysis filters={qualifiedFilters} />
            default:
                return null
        }
    }

    const getTabColorClasses = (tab: SubTab, isActive: boolean) => {
        const colors = {
            overview: isActive
                ? 'bg-blue-100 text-blue-700 border-blue-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            'sales-activity': isActive
                ? 'bg-purple-100 text-purple-700 border-purple-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
            pipeline: isActive
                ? 'bg-green-100 text-green-700 border-green-300'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
        }
        return colors[tab]
    }

    return (
        <div className="space-y-4">
            {/* Sub Tabs */}
            <div className="flex gap-2 flex-wrap">
                {subTabs.map((tab) => {
                    const isActive = activeSubTab === tab.value
                    return (
                        <button
                            key={tab.value}
                            onClick={() => setActiveSubTab(tab.value)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${getTabColorClasses(
                                tab.value,
                                isActive
                            )}`}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                        </button>
                    )
                })}
            </div>

            {/* Sub Tab Content */}
            {renderSubTabContent()}
        </div>
    )
}

export default QualifiedProspectsTab
